import React, { useCallback, useEffect, useState } from 'react';
import './RecipeManagement.css';
import {
  fetchRecipes,
  createRecipe,
  updateRecipe,
  deleteRecipe,
  explodeRecipe,
  InventoryApiError,
} from '../../services/inventoryApi';

interface RecipeManagementProps {
  user?: any;
  selectedStore?: string;
  enterpriseId: string;
}

interface RecipeIngredient {
  ingredientId: string;
  quantity: number;
  unit: string;
  notes?: string;
}

interface Recipe {
  enterpriseId?: string;
  recipeKey?: string;
  menuItemId: string;
  name: string;
  yield: number;
  yieldUnit: string;
  ingredients: RecipeIngredient[];
  prepTimeMinutes?: number | null;
  laborCost?: number | null;
  notes?: string | null;
  version?: number;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string;
}

interface ExplodeIngredient {
  ingredientId: string;
  quantity: number;
  unit: string;
}

interface ExplodeResult {
  recipeId: string;
  servings: number;
  ingredients: ExplodeIngredient[];
}

const BLANK_RECIPE: Recipe = {
  menuItemId: '',
  name: '',
  yield: 1,
  yieldUnit: 'serving',
  ingredients: [],
  prepTimeMinutes: null,
  laborCost: null,
  notes: '',
};

const formatTime = (minutes?: number | null) => {
  if (minutes == null) return '—';
  if (minutes < 60) return `${minutes}m`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
};

const RecipeManagement: React.FC<RecipeManagementProps> = ({ enterpriseId }) => {
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRecipe, setSelectedRecipe] = useState<Recipe | null>(null);

  const [formMode, setFormMode] = useState<'create' | 'edit' | null>(null);
  const [formRecipe, setFormRecipe] = useState<Recipe | null>(null);
  const [saving, setSaving] = useState(false);

  const [explodeTarget, setExplodeTarget] = useState<Recipe | null>(null);
  const [explodeServings, setExplodeServings] = useState<number>(1);
  const [explodeResult, setExplodeResult] = useState<ExplodeResult | null>(null);
  const [explodeLoading, setExplodeLoading] = useState(false);

  const load = useCallback(async () => {
    if (!enterpriseId) return;
    setLoading(true);
    setError(null);
    try {
      const res: any = await fetchRecipes(enterpriseId);
      const list: Recipe[] = res?.items || res?.data?.items || res?.data || [];
      setRecipes(Array.isArray(list) ? list : []);
    } catch (e) {
      setError(e instanceof InventoryApiError ? e.message : 'Failed to load recipes');
      setRecipes([]);
    } finally {
      setLoading(false);
    }
  }, [enterpriseId]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setFormMode('create');
    setFormRecipe({ ...BLANK_RECIPE, ingredients: [] });
    setError(null);
  };

  const openEdit = (r: Recipe) => {
    setFormMode('edit');
    setFormRecipe({ ...r, ingredients: [...(r.ingredients || [])] });
    setError(null);
  };

  const openDuplicate = (r: Recipe) => {
    setFormMode('create');
    setFormRecipe({
      ...r,
      menuItemId: '',
      name: r.name ? `${r.name} (copy)` : '',
      ingredients: (r.ingredients || []).map((ing) => ({ ...ing })),
      recipeKey: undefined,
      createdAt: undefined,
      updatedAt: undefined,
      version: undefined,
    });
    setError(null);
  };

  const closeForm = () => {
    setFormMode(null);
    setFormRecipe(null);
  };

  const save = async () => {
    if (!formRecipe || !enterpriseId) return;
    if (!formRecipe.menuItemId.trim()) {
      setError('menuItemId is required');
      return;
    }
    if (!formRecipe.name.trim()) {
      setError('Recipe name is required');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const body: any = {
        menuItemId: formRecipe.menuItemId.trim(),
        name: formRecipe.name.trim(),
        yield: Number(formRecipe.yield) || 1,
        yieldUnit: formRecipe.yieldUnit || 'serving',
        ingredients: (formRecipe.ingredients || [])
          .filter((ing) => ing.ingredientId.trim())
          .map((ing) => ({
            ingredientId: ing.ingredientId.trim(),
            quantity: Number(ing.quantity) || 0,
            unit: ing.unit || '',
            ...(ing.notes ? { notes: ing.notes } : {}),
          })),
        prepTimeMinutes: formRecipe.prepTimeMinutes != null ? Number(formRecipe.prepTimeMinutes) : null,
        laborCost: formRecipe.laborCost != null ? Number(formRecipe.laborCost) : null,
        notes: formRecipe.notes || null,
      };
      if (formMode === 'edit') {
        await updateRecipe(enterpriseId, formRecipe.menuItemId, body);
      } else {
        await createRecipe(enterpriseId, body);
      }
      closeForm();
      await load();
    } catch (e) {
      setError(e instanceof InventoryApiError ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (r: Recipe) => {
    if (!enterpriseId || !r.menuItemId) return;
    if (!window.confirm(`Delete recipe "${r.name}"?`)) return;
    setError(null);
    try {
      await deleteRecipe(enterpriseId, r.menuItemId);
      if (selectedRecipe?.menuItemId === r.menuItemId) setSelectedRecipe(null);
      await load();
    } catch (e) {
      setError(e instanceof InventoryApiError ? e.message : 'Delete failed');
    }
  };

  const openExplode = (r: Recipe) => {
    setExplodeTarget(r);
    setExplodeServings(r.yield || 1);
    setExplodeResult(null);
  };

  const closeExplode = () => {
    setExplodeTarget(null);
    setExplodeResult(null);
  };

  const runExplode = async () => {
    if (!explodeTarget || !enterpriseId) return;
    setExplodeLoading(true);
    setError(null);
    try {
      const res: any = await explodeRecipe(enterpriseId, explodeTarget.menuItemId, explodeServings);
      const payload = res?.data || res;
      setExplodeResult({
        recipeId: payload?.recipeId || explodeTarget.menuItemId,
        servings: payload?.servings || explodeServings,
        ingredients: Array.isArray(payload?.ingredients) ? payload.ingredients : [],
      });
    } catch (e) {
      setError(e instanceof InventoryApiError ? e.message : 'Explode failed');
    } finally {
      setExplodeLoading(false);
    }
  };

  const addIngredientRow = () =>
    setFormRecipe((f) =>
      f ? { ...f, ingredients: [...f.ingredients, { ingredientId: '', quantity: 0, unit: '' }] } : f
    );
  const updateIngredientRow = (idx: number, patch: Partial<RecipeIngredient>) =>
    setFormRecipe((f) =>
      f
        ? {
            ...f,
            ingredients: f.ingredients.map((ing, i) => (i === idx ? { ...ing, ...patch } : ing)),
          }
        : f
    );
  const removeIngredientRow = (idx: number) =>
    setFormRecipe((f) =>
      f ? { ...f, ingredients: f.ingredients.filter((_, i) => i !== idx) } : f
    );

  const filteredRecipes = recipes.filter((r) => {
    const q = searchTerm.toLowerCase();
    return (
      (r.name || '').toLowerCase().includes(q) ||
      (r.menuItemId || '').toLowerCase().includes(q)
    );
  });

  if (loading) {
    return (
      <div className="recipe-management">
        <p>Loading recipes…</p>
      </div>
    );
  }

  return (
    <div className="recipe-management">
      <div className="recipe-header">
        <div className="search-controls">
          <input
            type="text"
            placeholder="Search recipes…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <button className="btn-primary" onClick={openCreate}>+ New Recipe</button>
        </div>
      </div>

      {error && !formMode && !explodeTarget && (
        <div className="recipe-error" role="alert" style={{ padding: '0.75rem 1rem', marginBottom: '1rem', background: '#fee', color: '#900', borderRadius: 4 }}>
          {error}
        </div>
      )}

      <div className="recipe-content">
        <div className="recipes-view">
          <div className="recipes-list">
            {filteredRecipes.length === 0 ? (
              <div className="empty-state">
                <h3>No Recipes Found</h3>
                <p>Create a recipe to tie ingredient costs and prep steps to a menu item.</p>
                <button className="btn-primary" onClick={openCreate}>Create First Recipe</button>
              </div>
            ) : (
              <div className="recipes-grid">
                {filteredRecipes.map((recipe) => (
                  <div
                    key={recipe.menuItemId}
                    className={`recipe-card ${selectedRecipe?.menuItemId === recipe.menuItemId ? 'selected' : ''}`}
                    onClick={() => setSelectedRecipe(recipe)}
                  >
                    <div className="recipe-header">
                      <h3>{recipe.name}</h3>
                      {recipe.version != null && <span className="status">v{recipe.version}</span>}
                    </div>
                    <div className="recipe-meta">
                      <span className="category">{recipe.menuItemId}</span>
                      <span className="portion">{recipe.yield} {recipe.yieldUnit}</span>
                    </div>
                    <div className="recipe-timing">
                      <div className="time-item">
                        <span className="label">Prep:</span>
                        <span className="value">{formatTime(recipe.prepTimeMinutes)}</span>
                      </div>
                      {recipe.laborCost != null && (
                        <div className="time-item">
                          <span className="label">Labor:</span>
                          <span className="value">${Number(recipe.laborCost).toFixed(2)}</span>
                        </div>
                      )}
                    </div>
                    <div className="recipe-ingredients">
                      <span className="ingredients-count">{(recipe.ingredients || []).length} ingredients</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {selectedRecipe && (
            <div className="recipe-details">
              <div className="details-header">
                <h2>{selectedRecipe.name}</h2>
                <div className="details-actions">
                  <button className="btn-secondary" onClick={() => openEdit(selectedRecipe)}>Edit</button>
                  <button className="btn-secondary" onClick={() => openDuplicate(selectedRecipe)}>Duplicate</button>
                  <button className="btn-secondary" onClick={() => openExplode(selectedRecipe)}>Explode</button>
                  <button className="btn-danger" onClick={() => remove(selectedRecipe)}>Delete</button>
                </div>
              </div>

              <div className="recipe-info">
                <div className="info-grid">
                  <div className="info-item">
                    <label>Menu Item ID:</label>
                    <span>{selectedRecipe.menuItemId}</span>
                  </div>
                  <div className="info-item">
                    <label>Yield:</label>
                    <span>{selectedRecipe.yield} {selectedRecipe.yieldUnit}</span>
                  </div>
                  <div className="info-item">
                    <label>Prep Time:</label>
                    <span>{formatTime(selectedRecipe.prepTimeMinutes)}</span>
                  </div>
                  <div className="info-item">
                    <label>Labor Cost:</label>
                    <span>{selectedRecipe.laborCost != null ? `$${Number(selectedRecipe.laborCost).toFixed(2)}` : '—'}</span>
                  </div>
                  {selectedRecipe.updatedAt && (
                    <div className="info-item">
                      <label>Updated:</label>
                      <span>{new Date(selectedRecipe.updatedAt).toLocaleString()}</span>
                    </div>
                  )}
                </div>
                {selectedRecipe.notes && (
                  <div className="info-section">
                    <h4>Notes</h4>
                    <p>{selectedRecipe.notes}</p>
                  </div>
                )}
              </div>

              <div className="ingredients-section">
                <h4>Ingredients</h4>
                <div className="ingredients-table">
                  <div className="table-header">
                    <span>Ingredient ID</span>
                    <span>Quantity</span>
                    <span>Unit</span>
                    <span>Notes</span>
                  </div>
                  {(selectedRecipe.ingredients || []).length === 0 ? (
                    <div className="table-row"><span>No ingredients yet</span></div>
                  ) : (
                    (selectedRecipe.ingredients || []).map((ing, idx) => (
                      <div key={`${ing.ingredientId}-${idx}`} className="table-row">
                        <span className="item-name">{ing.ingredientId}</span>
                        <span className="quantity">{ing.quantity}</span>
                        <span className="unit">{ing.unit}</span>
                        <span>{ing.notes || ''}</span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {formMode && formRecipe && (
        <div className="recipe-modal-backdrop" onClick={closeForm} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="recipe-modal" onClick={(e) => e.stopPropagation()} style={{ background: 'white', borderRadius: 8, padding: '1.5rem', width: 'min(720px, 90vw)', maxHeight: '85vh', overflowY: 'auto' }}>
            <h2>{formMode === 'edit' ? 'Edit Recipe' : 'New Recipe'}</h2>

            {error && (
              <div role="alert" style={{ padding: '0.5rem 0.75rem', background: '#fee', color: '#900', borderRadius: 4, margin: '0.5rem 0' }}>
                {error}
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <label>Menu Item ID<span style={{ color: 'red' }}>*</span></label>
                <input
                  type="text"
                  value={formRecipe.menuItemId}
                  onChange={(e) => setFormRecipe({ ...formRecipe, menuItemId: e.target.value })}
                  disabled={formMode === 'edit'}
                  className="search-input"
                  style={{ width: '100%' }}
                />
              </div>
              <div>
                <label>Name<span style={{ color: 'red' }}>*</span></label>
                <input
                  type="text"
                  value={formRecipe.name}
                  onChange={(e) => setFormRecipe({ ...formRecipe, name: e.target.value })}
                  className="search-input"
                  style={{ width: '100%' }}
                />
              </div>
              <div>
                <label>Yield</label>
                <input
                  type="number"
                  min={0}
                  step={0.01}
                  value={formRecipe.yield}
                  onChange={(e) => setFormRecipe({ ...formRecipe, yield: Number(e.target.value) })}
                  className="search-input"
                  style={{ width: '100%' }}
                />
              </div>
              <div>
                <label>Yield Unit</label>
                <input
                  type="text"
                  value={formRecipe.yieldUnit}
                  onChange={(e) => setFormRecipe({ ...formRecipe, yieldUnit: e.target.value })}
                  className="search-input"
                  style={{ width: '100%' }}
                />
              </div>
              <div>
                <label>Prep Time (minutes)</label>
                <input
                  type="number"
                  min={0}
                  value={formRecipe.prepTimeMinutes ?? ''}
                  onChange={(e) => setFormRecipe({ ...formRecipe, prepTimeMinutes: e.target.value === '' ? null : Number(e.target.value) })}
                  className="search-input"
                  style={{ width: '100%' }}
                />
              </div>
              <div>
                <label>Labor Cost</label>
                <input
                  type="number"
                  min={0}
                  step={0.01}
                  value={formRecipe.laborCost ?? ''}
                  onChange={(e) => setFormRecipe({ ...formRecipe, laborCost: e.target.value === '' ? null : Number(e.target.value) })}
                  className="search-input"
                  style={{ width: '100%' }}
                />
              </div>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label>Notes</label>
              <textarea
                value={formRecipe.notes || ''}
                onChange={(e) => setFormRecipe({ ...formRecipe, notes: e.target.value })}
                rows={3}
                className="search-input"
                style={{ width: '100%', resize: 'vertical' }}
              />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                <h3 style={{ margin: 0 }}>Ingredients</h3>
                <button type="button" className="btn-sm" onClick={addIngredientRow}>+ Add ingredient</button>
              </div>
              {formRecipe.ingredients.length === 0 ? (
                <p style={{ fontStyle: 'italic', color: '#666' }}>No ingredients yet.</p>
              ) : (
                <div className="ingredients-table">
                  <div className="table-header">
                    <span>Ingredient ID</span>
                    <span>Quantity</span>
                    <span>Unit</span>
                    <span>Notes</span>
                    <span></span>
                  </div>
                  {formRecipe.ingredients.map((ing, idx) => (
                    <div key={idx} className="table-row" style={{ alignItems: 'center' }}>
                      <input
                        type="text"
                        value={ing.ingredientId}
                        onChange={(e) => updateIngredientRow(idx, { ingredientId: e.target.value })}
                        placeholder="ingredientId"
                        style={{ width: '100%' }}
                      />
                      <input
                        type="number"
                        value={ing.quantity}
                        onChange={(e) => updateIngredientRow(idx, { quantity: Number(e.target.value) })}
                        min={0}
                        step={0.001}
                        style={{ width: '100%' }}
                      />
                      <input
                        type="text"
                        value={ing.unit}
                        onChange={(e) => updateIngredientRow(idx, { unit: e.target.value })}
                        placeholder="oz, g, ea…"
                        style={{ width: '100%' }}
                      />
                      <input
                        type="text"
                        value={ing.notes || ''}
                        onChange={(e) => updateIngredientRow(idx, { notes: e.target.value })}
                        style={{ width: '100%' }}
                      />
                      <button type="button" className="btn-danger btn-sm" onClick={() => removeIngredientRow(idx)}>Remove</button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
              <button type="button" className="btn-secondary" onClick={closeForm} disabled={saving}>Cancel</button>
              <button type="button" className="btn-primary" onClick={save} disabled={saving}>
                {saving ? 'Saving…' : formMode === 'edit' ? 'Save changes' : 'Create recipe'}
              </button>
            </div>
          </div>
        </div>
      )}

      {explodeTarget && (
        <div className="recipe-modal-backdrop" onClick={closeExplode} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="recipe-modal" onClick={(e) => e.stopPropagation()} style={{ background: 'white', borderRadius: 8, padding: '1.5rem', width: 'min(560px, 90vw)' }}>
            <h2>Explode “{explodeTarget.name}”</h2>
            <p style={{ color: '#555', marginBottom: '1rem' }}>Compute ingredient totals for a target number of servings.</p>

            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end', marginBottom: '1rem' }}>
              <div style={{ flex: 1 }}>
                <label>Servings</label>
                <input
                  type="number"
                  min={1}
                  value={explodeServings}
                  onChange={(e) => setExplodeServings(Number(e.target.value) || 1)}
                  className="search-input"
                  style={{ width: '100%' }}
                />
              </div>
              <button type="button" className="btn-primary" onClick={runExplode} disabled={explodeLoading}>
                {explodeLoading ? 'Computing…' : 'Compute'}
              </button>
            </div>

            {error && (
              <div role="alert" style={{ padding: '0.5rem 0.75rem', background: '#fee', color: '#900', borderRadius: 4, margin: '0.5rem 0' }}>
                {error}
              </div>
            )}

            {explodeResult && (
              <div className="ingredients-section">
                <h4>Totals for {explodeResult.servings} servings</h4>
                <div className="ingredients-table">
                  <div className="table-header">
                    <span>Ingredient ID</span>
                    <span>Quantity</span>
                    <span>Unit</span>
                  </div>
                  {explodeResult.ingredients.length === 0 ? (
                    <div className="table-row"><span>No ingredients resolved.</span></div>
                  ) : (
                    explodeResult.ingredients.map((ing, idx) => (
                      <div key={`${ing.ingredientId}-${idx}`} className="table-row">
                        <span className="item-name">{ing.ingredientId}</span>
                        <span className="quantity">{ing.quantity}</span>
                        <span className="unit">{ing.unit}</span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
              <button type="button" className="btn-secondary" onClick={closeExplode}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecipeManagement;
